import { useCallback, useEffect, useRef, useState } from "react";
import { Gavel, Loader2, ShieldAlert, FileCheck2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { ETAPAS_EJECUCION, ORDEN_ETAPAS_EJECUCION, RESULTADOS_OPOSICION, fmtMoney } from "@/lib/fideicomisos";
import { toast } from "sonner";

const EtapaBadge = ({ etapa }) => {
  const m = ETAPAS_EJECUCION[etapa] || {};
  return <span data-testid={`ejec-etapa-badge-${etapa}`} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label || etapa}</span>;
};

function PdfPicker({ id, label, onDocId, busyKey, busy, fidId, tipoDoc }) {
  const ref = useRef(null);
  const subir = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", tipoDoc || label);
      fd.append("clasificacion", "Fideicomisos");
      fd.append("categoria", "fideicomiso_ejecucion");
      fd.append("referencia_id", fidId);
      const { data } = await api.post("/documentos/upload", fd);
      onDocId(data.id, data.nombre_archivo);
      toast.success(`PDF cargado: ${data.nombre_archivo}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={ref} type="file" accept="application/pdf" className="hidden"
             onChange={(e) => subir(e.target.files?.[0])} data-testid={`${id}-file-input`} />
      <Button data-testid={id} type="button" size="sm" variant="outline"
              disabled={busy === busyKey} onClick={() => ref.current?.click()}>
        {busy === busyKey ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}{label}
      </Button>
    </div>
  );
}

export function EjecucionTab({ fid, reload, puedeEjecutar }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  // iniciar
  const [credId, setCredId] = useState("");
  const [plazo, setPlazo] = useState("15");
  const [detDoc, setDetDoc] = useState({ id: "", nombre: "" });
  // convenio
  const [convDoc, setConvDoc] = useState({ id: "", nombre: "" });
  const [firma, setFirma] = useState(false);
  // notificar
  const [medio, setMedio] = useState("");
  const [domicilio, setDomicilio] = useState("");
  const [acuseDoc, setAcuseDoc] = useState({ id: "", nombre: "" });
  // oposición
  const [resultado, setResultado] = useState("");
  const [evidDoc, setEvidDoc] = useState({ id: "", nombre: "" });
  // enajenación
  const [comprador, setComprador] = useState("");
  const [precio, setPrecio] = useState("");
  const [contratoDoc, setContratoDoc] = useState({ id: "", nombre: "" });
  const [bienesSel, setBienesSel] = useState([]);
  // remanente
  const [reciboDoc, setReciboDoc] = useState({ id: "", nombre: "" });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/fideicomisos/${fid.id}/ejecucion`);
      setData(d);
    } catch (e) { console.debug(e); }
  }, [fid.id]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load(); await reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="p-6 text-sm text-slate-500">Cargando ejecución…</div>;
  const ejec = data.activa;
  const vinculosActivos = (fid.vinculos || []).filter((v) => v.estado_vinculo === "activo");
  const bienesPatrimonio = (fid.patrimonio || []).filter((b) => b.estado_bien === "en_patrimonio");
  const resInfo = RESULTADOS_OPOSICION.find((r) => r.value === resultado);

  return (
    <div className="space-y-4" data-testid="ejecucion-tab">
      {/* Convenio Art. 403 */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {data.convenio_ok ? <FileCheck2 className="h-4 w-4 text-emerald-600" /> : <ShieldAlert className="h-4 w-4 text-amber-600" />}
            <p className="text-sm font-semibold text-slate-800">Convenio de ejecución extrajudicial (Art. 403 LGTOC)</p>
          </div>
          {data.convenio_ok && <span data-testid="ejec-convenio-ok" className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">Firmado por el fideicomitente ✓</span>}
        </div>
        {!data.convenio_ok && (
          <div className="mt-3 space-y-3">
            <p className="text-xs text-slate-500">Sin este convenio (con firma ADICIONAL del fideicomitente en la sección especial) la vía de ejecución sería judicial — Art. 1414 Bis del Código de Comercio, fuera del sistema.</p>
            {puedeEjecutar && (
              <div className="flex flex-wrap items-center gap-3">
                <PdfPicker id="ejec-convenio-upload" label={convDoc.nombre ? `PDF: ${convDoc.nombre.slice(0, 24)}…` : "Subir convenio (PDF)"} fidId={fid.id}
                           tipoDoc="Convenio de ejecución extrajudicial (Art. 403)" onDocId={(id, n) => setConvDoc({ id, nombre: n })} busy={busy} busyKey="conv" />
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Checkbox data-testid="ejec-convenio-firma-check" checked={firma} onCheckedChange={(v) => setFirma(!!v)} />
                  Confirmo la firma adicional del fideicomitente (Art. 403)
                </label>
                <Button data-testid="ejec-convenio-ligar-btn" size="sm" disabled={!convDoc.id || busy === "ligar"}
                        onClick={() => run("ligar", () => api.post(`/fideicomisos/${fid.id}/ejecucion/convenio`, { documento_id: convDoc.id, firma_adicional_fideicomitente: firma }), "Convenio ligado con firma adicional.")}>
                  Ligar convenio
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Ejecución activa o inicio */}
      {!ejec ? (
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Gavel className="h-4 w-4" /> Iniciar ejecución extrajudicial</p>
          <p className="mb-4 text-xs text-slate-500">Requiere: convenio firmado (Art. 403) + determinación de incumplimiento emitida por el ejecutor independiente (Art. 395) + crédito vinculado activo. El fideicomiso pasará a "En ejecución".</p>
          {puedeEjecutar ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <Select value={credId} onValueChange={setCredId}>
                <SelectTrigger data-testid="ejec-credito-select"><SelectValue placeholder="Crédito vinculado…" /></SelectTrigger>
                <SelectContent>
                  {vinculosActivos.map((v) => (
                    <SelectItem key={v.credito_id} value={v.credito_id}>{v.credito?.numero_credito} — {fmtMoney(v.credito?.saldo_insoluto)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input data-testid="ejec-plazo-input" type="number" min="1" value={plazo} onChange={(e) => setPlazo(e.target.value)} placeholder="Plazo de oposición (días naturales)" />
              <PdfPicker id="ejec-determinacion-upload" label={detDoc.nombre ? `PDF: ${detDoc.nombre.slice(0, 20)}…` : "Determinación del ejecutor (PDF)"} fidId={fid.id}
                         tipoDoc="Determinación de incumplimiento (ejecutor independiente)" onDocId={(id, n) => setDetDoc({ id, nombre: n })} busy={busy} busyKey="det" />
              <Button data-testid="ejec-iniciar-btn" disabled={!credId || !detDoc.id || busy === "iniciar"}
                      onClick={() => run("iniciar", () => api.post(`/fideicomisos/${fid.id}/ejecucion`, { credito_id: credId, plazo_oposicion_dias: plazo, determinacion_ejecutor_doc_id: detDoc.id }), "Ejecución iniciada (etapa: solicitada).")}>
                {busy === "iniciar" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Iniciar ejecución
              </Button>
            </div>
          ) : <p className="text-xs text-slate-400">Solo Admin de la entidad u Oficial de cumplimiento pueden iniciar la ejecución.</p>}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="ejec-activa-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-800">Ejecución en curso — crédito {ejec.credito_id.slice(0, 8)}…</p>
            <EtapaBadge etapa={ejec.etapa} />
          </div>
          {/* Timeline */}
          <div className="mb-5 flex flex-wrap items-center gap-1.5 text-[11px]">
            {ORDEN_ETAPAS_EJECUCION.map((et, i) => {
              const idx = ORDEN_ETAPAS_EJECUCION.indexOf(ejec.etapa === "notificada" ? "en_oposicion" : ejec.etapa);
              const done = i <= idx;
              return (
                <span key={et} className="flex items-center gap-1.5">
                  {i > 0 && <ArrowRight className="h-3 w-3 text-slate-300" />}
                  <span className={`rounded-full px-2 py-0.5 font-medium ${done ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-400"}`}>{ETAPAS_EJECUCION[et].label}</span>
                </span>
              );
            })}
          </div>

          {ejec.etapa === "solicitada" && puedeEjecutar && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <Input data-testid="ejec-notif-medio" value={medio} onChange={(e) => setMedio(e.target.value)} placeholder="Medio (fedatario, correo certificado…)" />
              <Input data-testid="ejec-notif-domicilio" value={domicilio} onChange={(e) => setDomicilio(e.target.value)} placeholder="Domicilio usado" />
              <PdfPicker id="ejec-acuse-upload" label={acuseDoc.nombre ? `PDF: ${acuseDoc.nombre.slice(0, 20)}…` : "Acuse de notificación (PDF)"} fidId={fid.id}
                         tipoDoc="Acuse de notificación de ejecución" onDocId={(id, n) => setAcuseDoc({ id, nombre: n })} busy={busy} busyKey="acu" />
              <Button data-testid="ejec-notificar-btn" disabled={!medio || !acuseDoc.id || busy === "notif"}
                      onClick={() => run("notif", () => api.post(`/fideicomisos/${fid.id}/ejecucion/${ejec.id}/notificar`, { medio, domicilio_usado: domicilio, acuse_doc_id: acuseDoc.id }), "Notificación registrada: ventana de oposición corriendo.")}>
                Registrar notificación
              </Button>
            </div>
          )}

          {ejec.etapa === "en_oposicion" && (
            <div className="space-y-3">
              <p className="text-xs text-slate-600" data-testid="ejec-plazo-limite">Ventana de oposición: <strong>{ejec.plazos?.plazo_oposicion_dias} días naturales</strong> — vence el <strong>{ejec.plazos?.fecha_limite_oposicion}</strong>.</p>
              {puedeEjecutar && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Select value={resultado} onValueChange={setResultado}>
                    <SelectTrigger data-testid="ejec-oposicion-select"><SelectValue placeholder="Resultado de la oposición…" /></SelectTrigger>
                    <SelectContent>
                      {RESULTADOS_OPOSICION.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {resInfo?.evidencia && (
                    <PdfPicker id="ejec-evidencia-upload" label={evidDoc.nombre ? `PDF: ${evidDoc.nombre.slice(0, 20)}…` : "Evidencia (PDF, obligatoria)"} fidId={fid.id}
                               tipoDoc="Evidencia de oposición a la ejecución" onDocId={(id, n) => setEvidDoc({ id, nombre: n })} busy={busy} busyKey="evid" />
                  )}
                  <Button data-testid="ejec-oposicion-btn" disabled={!resultado || (resInfo?.evidencia && !evidDoc.id) || busy === "opo"}
                          onClick={() => run("opo", () => api.post(`/fideicomisos/${fid.id}/ejecucion/${ejec.id}/oposicion`, { resultado, evidencia_doc_id: evidDoc.id }), "Resultado de oposición registrado.")}>
                    Registrar resultado
                  </Button>
                </div>
              )}
            </div>
          )}

          {ejec.etapa === "en_enajenacion" && !ejec.enajenacion?.precio && puedeEjecutar && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-700">Enajenación de bienes del patrimonio</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <Input data-testid="ejec-comprador-input" value={comprador} onChange={(e) => setComprador(e.target.value)} placeholder="Comprador" />
                <Input data-testid="ejec-precio-input" type="number" min="0" value={precio} onChange={(e) => setPrecio(e.target.value)} placeholder="Precio de enajenación" />
                <PdfPicker id="ejec-contrato-upload" label={contratoDoc.nombre ? `PDF: ${contratoDoc.nombre.slice(0, 20)}…` : "Contrato de enajenación (PDF)"} fidId={fid.id}
                           tipoDoc="Contrato de enajenación (ejecución)" onDocId={(id, n) => setContratoDoc({ id, nombre: n })} busy={busy} busyKey="cont" />
                <Button data-testid="ejec-enajenar-btn" disabled={!comprador || !precio || !contratoDoc.id || bienesSel.length === 0 || busy === "ena"}
                        onClick={() => run("ena", () => api.post(`/fideicomisos/${fid.id}/ejecucion/${ejec.id}/enajenar`, { comprador_texto: comprador, precio, contrato_doc_id: contratoDoc.id, bien_ids: bienesSel }), "Enajenación registrada: bienes → enajenado.")}>
                  Registrar enajenación
                </Button>
              </div>
              <div className="flex flex-wrap gap-3">
                {bienesPatrimonio.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-700">
                    <Checkbox data-testid={`ejec-bien-check-${b.id}`} checked={bienesSel.includes(b.id)}
                              onCheckedChange={(v) => setBienesSel((s) => (v ? [...s, b.id] : s.filter((x) => x !== b.id)))} />
                    {b.descripcion?.slice(0, 40)} · {fmtMoney(b.valor_avaluo)}
                  </label>
                ))}
                {bienesPatrimonio.length === 0 && <p className="text-xs text-amber-600">No hay bienes en patrimonio para enajenar.</p>}
              </div>
            </div>
          )}

          {ejec.etapa === "en_enajenacion" && ejec.enajenacion?.precio && !ejec.aplicacion?.pago_id && puedeEjecutar && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs text-slate-600">Enajenado a <strong>{ejec.enajenacion.comprador_texto}</strong> por <strong>{fmtMoney(ejec.enajenacion.precio)}</strong>. Aplica los recursos al crédito por el motor de pagos.</p>
              <Button data-testid="ejec-aplicar-btn" disabled={busy === "apl"}
                      onClick={() => run("apl", () => api.post(`/fideicomisos/${fid.id}/ejecucion/${ejec.id}/aplicar-recursos`, {}), "Recursos aplicados al crédito (pago registrado).")}>
                {busy === "apl" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Aplicar recursos al crédito
              </Button>
            </div>
          )}

          {ejec.etapa === "en_enajenacion" && ejec.aplicacion?.pago_id && (ejec.remanente?.monto > 0) && !ejec.remanente?.recibo_doc_id && puedeEjecutar && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs text-slate-600" data-testid="ejec-remanente-monto">Excedente a entregar al fideicomitente: <strong>{fmtMoney(ejec.remanente.monto)}</strong> (recibo PDF obligatorio).</p>
              <PdfPicker id="ejec-recibo-upload" label={reciboDoc.nombre ? `PDF: ${reciboDoc.nombre.slice(0, 20)}…` : "Recibo de entrega (PDF)"} fidId={fid.id}
                         tipoDoc="Recibo de entrega del remanente" onDocId={(id, n) => setReciboDoc({ id, nombre: n })} busy={busy} busyKey="rec" />
              <Button data-testid="ejec-remanente-btn" disabled={!reciboDoc.id || busy === "rem"}
                      onClick={() => run("rem", () => api.post(`/fideicomisos/${fid.id}/ejecucion/${ejec.id}/remanente`, { recibo_doc_id: reciboDoc.id }), "Remanente entregado al fideicomitente.")}>
                Registrar entrega del remanente
              </Button>
            </div>
          )}

          {ejec.etapa === "recursos_aplicados" && (
            <div className="space-y-2">
              {ejec.aplicacion?.pago_id && (
                <p className="text-xs text-slate-600" data-testid="ejec-aplicacion-info">
                  Aplicado al crédito: <strong>{fmtMoney(ejec.aplicacion.monto_aplicado)}</strong> (pago {ejec.aplicacion.pago_id.slice(0, 8)}…) · Saldo remanente del crédito: <strong>{fmtMoney(ejec.aplicacion.saldo_remanente_credito)}</strong>
                </p>
              )}
              {ejec.nota_saldo_remanente && <p className="text-xs font-medium text-amber-700" data-testid="ejec-nota-saldo">{ejec.nota_saldo_remanente}</p>}
              {puedeEjecutar && (
                <Button data-testid="ejec-concluir-btn" disabled={busy === "con"}
                        onClick={() => run("con", () => api.post(`/fideicomisos/${fid.id}/ejecucion/${ejec.id}/concluir`, {}), "Ejecución concluida: vínculo → ejecutado.")}>
                  Concluir ejecución
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Históricas */}
      {(data.historicas || []).length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Ejecuciones cerradas</p>
          <div className="space-y-2">
            {data.historicas.map((h) => (
              <div key={h.id} className="flex items-center justify-between rounded-md border border-slate-100 p-3 text-xs" data-testid={`ejec-historica-${h.id}`}>
                <span className="text-slate-600">Crédito {h.credito_id.slice(0, 8)}… · {h.created_at?.slice(0, 10)} {h.motivo_cierre ? `· ${h.motivo_cierre}` : ""}</span>
                <EtapaBadge etapa={h.etapa} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
